import { useCallback, useEffect, useState } from "react";
import { DOCUMENTS_CHANGED_EVENT } from "../../../services/agencyDocumentUploadService";
import { AgencyDocumentRow, AgencyTagRow, documentsApi } from "./api";

const POLL_MS = 5000;

// The agency's documents and tag vocabulary. Polls while anything is still
// being processed and refreshes when the upload service says so.
export function useAgencyDocuments() {
  const [documents, setDocuments] = useState<AgencyDocumentRow[]>([]);
  const [tags, setTags] = useState<AgencyTagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [docs, vocab] = await Promise.all([documentsApi.list(), documentsApi.tags()]);
      setDocuments(docs);
      setTags(vocab);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const onChanged = () => reload();
    window.addEventListener(DOCUMENTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(DOCUMENTS_CHANGED_EVENT, onChanged);
  }, [reload]);

  const busy = documents.some((d) => d.status === "pending" || d.status === "processing");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [busy, reload]);

  return { documents, tags, loading, error, reload, setDocuments, setTags };
}
